import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';
import GoogleAppsLauncher from './google-apps-launcher';

const ProfileDropdown = ({ user, onSignOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showGoogleApps, setShowGoogleApps] = useState(false);
  const dropdownRef = useRef(null);

  // Debug: Log user object to see what fields are available
  console.log('ProfileDropdown user object:', user);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSignOut = () => {
    setIsOpen(false);
    onSignOut();
  };

  const handleGoogleAppsClick = () => {
    setIsOpen(false);
    setShowGoogleApps(true);
  };

  const handleCloseGoogleApps = () => {
    setShowGoogleApps(false);
  };

  if (!user) return null;

  return (
    <div ref={dropdownRef} className="relative">
      {/* Profile Button */}
      <button
        onClick={handleToggle}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 transition-colors duration-200"
      >
        {/* Profile Avatar */}
        <div className="relative">
          {user.photoURL && !imageError ? (
            <img
              src={user.photoURL}
              alt={user.displayName || user.name || 'Profile'}
              className="w-8 h-8 rounded-full border border-zinc-800 object-cover"
              onError={() => {
                console.log('Profile image failed to load:', user.photoURL);
                setImageError(true);
              }}
              onLoad={() => {
                console.log('Profile image loaded successfully:', user.photoURL);
                setImageError(false);
              }}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-zinc-700 border border-zinc-600 border-dashed flex items-center justify-center">
              <span className="text-zinc-300 text-sm font-medium">
                {(user.displayName || user.name || user.email || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {/* Online indicator */}
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-zinc-900 rounded-full"></div>
        </div>
        
        {/* User Name (hidden on mobile) */}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-zinc-200">
            {user.displayName || user.name || 'User'}
          </p>
          <p className="text-xs text-zinc-500">
            {user.email}
          </p>
        </div>

        {/* Dropdown Arrow */}
        <svg
          className={cn(
            "w-4 h-4 text-zinc-400 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-zinc-900 border border-zinc-800 border-dashed rounded-lg shadow-xl z-50">
          {/* User Info Section */}
          <div className="p-4 border-b border-dashed border-zinc-800">
            <div className="flex items-center gap-3">
              {/* Large Avatar */}
              <div className="relative">
                {user.photoURL && !imageError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || user.name || 'Profile'}
                    className="w-12 h-12 rounded-full border border-zinc-600 object-cover"
                    onError={() => setImageError(true)}
                    onLoad={() => setImageError(false)}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center">
                    <span className="text-zinc-300 text-lg font-medium">
                      {(user.displayName || user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 border-2 border-zinc-900 rounded-full"></div>
              </div>
              
              {/* User Details */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-200 truncate">
                  {user.displayName || user.name || 'User'}
                </p>
                <p className="text-xs text-zinc-500 truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Options */}
          <div className="py-2">
            {/* Google Apps Button */}
            <button
              onClick={handleGoogleAppsClick}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors duration-150"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              <span>Google Apps</span>
            </button>
            
            {/* Logout Button */}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors duration-150"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Google Apps Launcher */}
      <GoogleAppsLauncher 
        isOpen={showGoogleApps} 
        onClose={handleCloseGoogleApps} 
      />
    </div>
  );
};

export default ProfileDropdown;
